import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./checkoutPage.css";
import { CombinedContext } from "../../context/combinedContext";
import billingRequest from "../../request/billing";
import translations from "../../translations/translations";
import CheckoutSkeleton from "./checkoutPageSkeleton/checkoutPageSkeleton";

const CheckoutPage = () => {
	const { userData, language } = useContext(CombinedContext);

	const [showStillThere, setShowStillThere] = useState(false);

	const intervalRef = useRef(null);
	const startTimeRef = useRef(null);

	const user = userData?.user;
	const shop = userData?.shop;

	const location = useLocation();
	const navigate = useNavigate();
	const openedRef = useRef(false);

	const billingData = location.state?.billingData;
	// Reactivating a suspended shop reuses this screen, but that shop finished
	// onboarding long ago — `setup` is gone and billingSetup must not re-run.
	const isReactivation = !!location.state?.reactivate;
	const [loading, setLoading] = useState(!billingData);
	const [localBillingData, setLocalBillingData] = useState(billingData);

	const startPolling = () => {
		startTimeRef.current = Date.now();
		setShowStillThere(false);

		intervalRef.current = setInterval(async () => {
			const elapsed = Date.now() - startTimeRef.current;

			// ⏱ Stop after 15 minutes
			if (elapsed >= 15 * 60 * 1000) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
				setShowStillThere(true);
				return;
			}

			try {
				const status = await billingRequest.billingStatus(shop.shopId, userData.cognitoTokens.AccessToken);

				if (status.active) {
					clearInterval(intervalRef.current);
					window.location.href = "/calendar?showLiveBanner=true";
				}
			} catch (err) {
				console.error("Polling error:", err);
			}
		}, 4000); // 🔥 Fixed 4 seconds
	};

	useEffect(() => {
		if (!user || !shop) return;

		if (user.role !== "owner" || (!shop.setup && !isReactivation)) {
			navigate("/calendar");
		}
	}, [user, shop, navigate, isReactivation]);

	useEffect(() => {
		const recover = async () => {
			// On a reactivation the transaction came from /billing/reactivate, not
			// billingSetup. Re-running setup here would reset billingStatus and cost
			// the shop its un-suspend on payment, so send them back to start over.
			if (!billingData && isReactivation) {
				navigate("/account/billing");
				return;
			}

			if (!billingData && shop && user) {
				try {
					setLoading(true);

					const res = await billingRequest.billingSetup({
						shopId: shop.shopId,
						userId: user.userId,
						accessToken: userData.cognitoTokens.AccessToken,
					});

					setLocalBillingData(res); // 👈 no navigation
				} catch (err) {
					console.error("Failed to recover billing setup", err);
					navigate("/calendar");
				} finally {
					setLoading(false);
				}
			}
		};

		recover();
	}, [billingData, shop, user, navigate, isReactivation]);

	useEffect(() => {
		if (!localBillingData) return;
		if (openedRef.current) return;

		const paddleLocaleMap = {
			en: "en",
			es: "es",
			fr: "fr",
		};

		const paddleLocale = paddleLocaleMap[language] || "en";

		// Arriving from the activation email is a cold page load, so Paddle.js may
		// still be loading when billingSetup resolves. Wait for it rather than
		// bailing — this effect only re-runs on localBillingData/shop/language, so
		// a bail left the user staring at an empty checkout box until they
		// refreshed. __PADDLE_READY__ is set by useInitPaddle after Initialize().
		let readyPoll = null;

		const isPaddleReady = () => window.__PADDLE_READY__ && window.Paddle?.Checkout?.open;

		const openCheckout = () => {
			openedRef.current = true;

			window.Paddle.Checkout.open({
				settings: {
					displayMode: "inline",
					frameTarget: "paddle-checkout-container",
					frameInitialHeight: 650,
					locale: paddleLocale,
				},
				transactionId: localBillingData.transactionId,
			});

			// ✅ Start polling immediately
			startPolling();
		};

		if (isPaddleReady()) {
			openCheckout();
		} else {
			// Bounded: if Paddle is blocked (adblocker, CDN down) stop rather than
			// polling for the life of the page.
			let waited = 0;

			readyPoll = setInterval(() => {
				waited += 100;

				if (!isPaddleReady()) {
					if (waited < 15000) return;
					console.error("Paddle failed to load — checkout could not open");
				}

				clearInterval(readyPoll);
				readyPoll = null;

				if (isPaddleReady()) openCheckout();
			}, 100);
		}

		// ✅ Cleanup if component unmounts
		return () => {
			if (readyPoll) clearInterval(readyPoll);
		};
	}, [localBillingData, shop, language]);

	const handleContinue = () => {
		startPolling();
	};

	if (loading || !localBillingData) {
        return (
            <div className="sf-checkout-page">
                <div className="sf-checkout-card">
                    <CheckoutSkeleton />
                </div>
            </div>
        );
    }


	return (
		<div className="sf-checkout-page">
			<div className="sf-checkout-card">
				<h1>{translations[language].checkoutPage.activateTitle}</h1>

				<div className="sf-checkout-paddle-wrapper">
					<div className="paddle-checkout-container"></div>
					{showStillThere && (
						<div className="sf-checkout-still-there">
							<p>{translations[language].checkoutPage.stillThere}</p>
							<button className="sf-checkout-continue-btn" onClick={handleContinue}>
								{translations[language].checkoutPage.continue}
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CheckoutPage;
